<template>
  <div class="answer-textbox">
    <div
      :key="`answer-id-${currentQuestion.uuid || currentQuestion.question_id}`"
    >
      <div
        v-if="isDynamicField && !isLabRunning && !submittedAnswer"
        class="answer-textbox answer-textbox__dynamic-flag-lab-off"
      >
        <AlertCircleIcon size="16" />
        <span>
          You can only answer Dynamic Flag questions while the lab is running.
          Please start the lab to submit the answer.
        </span>
      </div>
      <div
        v-if="isDynamicField ? isLabRunning : true"
        :class="[
          'answer-textbox__input-container',
          submittedAnswer && 'answer-textbox__input-container--hide',
        ]"
      >
        <div
          :class="`
            answer-textbox__dynamic-layout-submit${
              !isDynamicField ? '--hidden' : ''
            }
          `"
        >
          <div class="answer-textbox__dynamic-layout-terms">
            <label
              class="answer-textbox__dynamic-layout-terms-checkbox answer-checkbox"
            >
              <span
                class="answer-textbox__dynamic-layout-terms-input answer-checkbox__input"
              >
                <input
                  id="answer-textbox__dynamic-layout-terms-id"
                  v-model="termsAccepted"
                  type="checkbox"
                  name="checkbox"
                  :checked="termsAccepted"
                />
                <span
                  class="answer-textbox__dynamic-layout-terms-control answer-checkbox__control"
                >
                  <CheckboxTick />
                </span>
              </span>
              <div class="answer-textbox__dynamic-layout-terms-text">
                I understand that I only have one attempt at this answer. After
                clicking <i>Submit answer</i>, I will not be able to make edits.
                Your answer will be graded based on the state of the lab
                environment at the time it is submitted.
              </div>
            </label>
          </div>
        </div>
        <input
          ref="answer"
          type="text"
          class="answer-textbox__input"
          placeholder="Your answer"
          :disabled="isDynamicField ? !termsAccepted : false"
          :value="userAnswer"
        />
        <button
          class="btn btn--secondary"
          :disabled="
            isDynamicField ? !termsAccepted || isLoading : submittedAnswer
          "
          @click="submitAnswer()"
        >
          Submit answer
        </button>
        <div
          v-if="isCaseSensitive"
          class="answer-textbox__case-sensitive-disclaimer"
        >
          <AlertCircleIcon size="12" />
          <p class="answer-textbox__case-sensitive-disclaimer__text">
            Case-sensitive answer. Please enter text as displayed.
          </p>
        </div>
      </div>
      <div
        :class="[
          'answer-textbox__submitted-container',
          isDynamicField &&
            'answer-textbox__submitted-container--dynamic-field',
          submittedAnswer && 'answer-textbox__submitted-container--show',
          !isLabRunning && 'answer-textbox__submitted-container--replace',
        ]"
      >
        <div class="answer-textbox__submitted-answer">
          <div class="answer-textbox__submitted-pill">Submitted</div>
          <div class="answer-textbox__submitted-answer-text">
            {{
              isDynamicField
                ? `Your answer was submitted${
                    userAnswerDateString && ` on the ${userAnswerDateString}`
                  }. Your answer will be graded based on the state of the lab environment at the time it was submitted.`
                : `Your answer: ${userAnswer}`
            }}
          </div>
        </div>
        <button
          :class="[
            'btn',
            'btn--secondary',
            `answer-textbox__submitted-edit-button${
              isDynamicField ? '--hidden' : ''
            }`,
          ]"
          :disabled="isLoading"
          @click="editAnswer()"
        >
          Edit answer
        </button>
      </div>
      <div
        v-if="isDynamicField"
        :class="[
          'answer-textbox__dynamic-layout-after-submit-information',
          submittedAnswer &&
            'answer-textbox__dynamic-layout-after-submit-information--show',
          !isLabRunning &&
            'answer-textbox__dynamic-layout-after-submit-information--replace',
        ]"
      >
        <AlertCircleIcon size="14" />
        <div
          class="answer-textbox__dynamic-layout-after-submit-information--main"
        >
          You do not need to change the value of the dynamic flag once it has
          been submitted.
        </div>
        <div
          class="answer-textbox__dynamic-layout-after-submit-information--secondary"
        >
          If a lab session is reset anytime during an exam, you do NOT need to
          re-submit your answer(s) for dynamic flag questions that you have
          already submitted. Dynamic flags are tied to your current exam lab
          session. Resetting your exam lab environment will change the values of
          dynamic flags in the exam lab environment.
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { AlertCircleIcon } from 'vue-feather-icons'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import { format } from 'date-fns-tz'

export default {
  name: 'AnswerTextbox',
  components: {
    AlertCircleIcon,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
  },
  props: {
    currentQuestion: {
      type: Object,
      required: true,
    },
    isLabRunning: {
      type: Boolean,
      required: false,
      default: false,
    },
    attempt: {
      type: Object,
      required: true,
    },
    currentQuestionId: {
      type: String,
      required: true,
    },
    voucherId: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data() {
    return {
      termsAccepted: false,
      inputValue: '',
      submittedAnswer: this.currentQuestion.answered,
      userAnswer: this.currentQuestion.user_answer
        ? this.currentQuestion.user_answer
        : '',
      userAnswerDateString: this.currentQuestion.user_answer
        ? format(
            new Date(this.currentQuestion.answered_at),
            'do $ LLL yyyy'
          ).replace('$', 'of')
        : '',
    }
  },
  computed: {
    ...mapGetters({
      answeredAt: 'certifications/answeredAt',
    }),
    isDynamicField() {
      return this.currentQuestion.is_dynamic
    },
    isCaseSensitive() {
      const { widget, is_case_sensitive } = this.currentQuestion
      return widget === 'input' && is_case_sensitive !== false
    },
  },
  watch: {
    currentQuestion(question, oldQuestion) {
      if (
        (oldQuestion.uuid || oldQuestion.question_id) !== this.currentQuestionId
      ) {
        this.userAnswerDateString = this.currentQuestion.answered_at
          ? format(
              new Date(this.currentQuestion.answered_at),
              'do $ LLL yyyy'
            ).replace('$', 'of')
          : ''
        this.userAnswer = this.currentQuestion.user_answer
        this.submittedAnswer = this.currentQuestion.answered
        this.termsAccepted = false
      }
    },
  },
  methods: {
    async postAnswer(answerString) {
      this.submittedAnswer = true
      this.$emit('handle-nav-loading', true)
      const payload = {
        attemptId: this.attempt.id,
        voucherId: this.voucherId,
        answer: {
          question_id: this.currentQuestion.uuid,
          answer: answerString,
        },
      }
      try {
        await this.$store.dispatch('certifications/submitQuestion', payload)
        this.currentQuestion.answered = true
        this.currentQuestion.user_answer = answerString
        this.userAnswerDateString = this.answeredAt(
          this.currentQuestion.uuid || this.currentQuestion.question_id
        )
        this.$emit('update-selection')
      } catch (error) {
        this.submittedAnswer = false
        this.$emit('exam-error')
      }
      this.$emit('handle-nav-loading', false)
    },
    submitAnswer() {
      this.inputValue = this.$refs.answer.value
      if (this.inputValue) {
        this.submittedAnswer = true
        this.userAnswer = this.inputValue
        this.postAnswer(this.inputValue)
        this.inputValue = ''
      }
      // TODO: turn input red
    },
    editAnswer() {
      this.submittedAnswer = false
      const answerInputRef = this.$refs.answer
      answerInputRef.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
.answer-textbox {
  &__dynamic {
    &-flag-lab-off {
      svg {
        margin-right: 10px;
      }
      font-weight: 450;
      font-size: 14px;
      line-height: 20px;
      display: flex;
      align-items: center;
    }
    &-layout {
      &-submit {
        grid-area: 1/1/2/3;
        display: flex;
        &--hidden {
          display: none;
        }
      }
      &-after-submit-information {
        display: none;
        color: var(--white);
        font-size: rem(14px);
        line-height: 20px;
        grid-template-columns: 25px 85%;
        svg {
          margin-top: 5px;
        }
        &--show {
          display: grid;
          position: relative;
          top: -68px;
        }
        &--replace {
          top: 0;
          margin-top: 60px;
          margin-bottom: 130px;
        }
        &--main {
          margin-bottom: 10px;
        }
        &--secondary {
          color: var(--light-blue-gray);
          grid-area: 2/2/2/3;
        }
      }
      &-terms {
        line-height: 20px;
        &-text {
          font-size: rem(14px);
        }
        label {
          margin-bottom: 10px;
        }
      }
    }
  }
  &__input {
    $input: &;
    &-container {
      width: 100%;
      display: grid;
      grid-template-columns: 80% 170px;
      transition: all 0.6s ease-out;
      opacity: 1;
      z-index: 1;
      &--hide {
        opacity: 0;
        z-index: -1;
        pointer-events: none;
      }
      button {
        margin-left: 10px;
      }
    }
    height: 46px;
    width: 100%;
    padding: 15px 10px;
    border-radius: 5px;
    background: var(--darker-gray);
    color: var(--white);
    // TODO: this could be a mixin for text input
    border: 1px solid var(--emperor-gray);
    box-sizing: border-box;
    box-shadow: var(--gray-light-box-shadow);

    &:focus {
      color: var(--light-blue-gray);
      box-shadow: var(--purple-dark-box-shadow);
      &#{$input}--error {
        box-shadow: var(--carnation-box-shadow);
        filter: var(--gray-light-box-shadow-filter);
      }
    }
    &:disabled {
      background: var(--darker-gray);
      border: 1px solid var(--flat-black);
      box-sizing: border-box;
      box-shadow: var(--gray-light-box-shadow);
      color: var(--tundora-gray);
    }
    &--error {
      box-shadow: var(--carnation-box-shadow);
      filter: var(--gray-light-box-shadow-filter);
    }
  }
  &__case-sensitive-disclaimer {
    display: flex;
    gap: 4px;
    margin-top: 4px;
    align-items: center;
    &__text {
      color: var(--perfect-gray);
      font-size: rem(12px);
      font-weight: 450;
      line-height: rem(14px);
      margin: 0;
    }
  }
  &__button {
    &--hidden {
      display: none;
    }
  }
  &__submitted {
    &-container {
      width: 100%;
      height: 65px;
      border-radius: 10px;
      background: var(--glass-mid-gray);
      padding: 20px;
      position: relative;
      top: -56px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: opacity 0.6s ease-out;
      opacity: 0;
      z-index: -1;
      &--dynamic-field {
        top: -128px;
      }
      &--show {
        opacity: 1;
        z-index: 1;
      }
      &--replace {
        top: 0;
      }
    }
    &-pill {
      width: 80px;
      color: var(--charcoal);
      text-align: center;
      padding: 5px;
      font-weight: 450;
      font-size: 12px;
      line-height: 14px;
      // TODO: this could be a mixin for all purple pills
      background: var(--purple-light);
      box-shadow: var(--purple-light-box-shadow);
      border-radius: 30px;
    }
    &-answer {
      display: flex;
      align-items: center;
      &-text {
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
        margin-left: 10px;
      }
    }
    &-edit-button {
      &--hidden {
        display: none;
      }
    }
  }
}
</style>
